/**
 * Interactive tutorials — the Learn group in the profile slide-over.
 *
 * The markup is server-rendered ERB (app/views/application/_account_menu.html.erb),
 * one `.tour-row` per Tutorials::TOURS entry, each with a Start/Redo button
 * carrying `data-tour-action="start"` and `data-tour-id`. This module is the
 * only thing that makes those buttons do anything.
 *
 * ONE delegated listener on `document`, not a listener per button. The drawer is
 * re-rendered by every full page load and its inline script re-binds itself on
 * `turbo:load`, so per-button binding would need the same re-binding dance and
 * would silently miss a row added by a Turbo frame. `document` survives a Turbo
 * Drive visit (only <body> is swapped), which is also why the guard below is a
 * module-level flag rather than a check for an element.
 *
 * Nothing here opens or closes the drawer itself: closeProfileDrawer() calls the
 * very functions _account_menu.html.erb's own close button calls.
 */

const START_SELECTOR = '[data-tour-action="start"]'
const DRAWER_ID = 'profile-sidebar'

declare global {
  interface Window {
    /** Defined by _account_menu.html.erb's initializeProfileSidebar(). */
    closeSidebar?: () => void
    /** The element-only closer the same script publishes beside it. */
    forceCloseSidebar?: () => void
  }
}

/**
 * Close the profile drawer the way the drawer closes itself.
 *
 * `closeSidebar` and `forceCloseSidebar` are both published on `window` by the
 * inline script in _account_menu.html.erb, and its own close button tries them
 * in exactly this order. Reusing them rather than re-implementing the class and
 * style changes matters because closing is not one operation: it also restores
 * `body.overflow` and the left nav that opening hid.
 *
 * The two are assigned together at the end of initializeProfileSidebar(), so
 * today there is no moment where only the fallback exists — it is tried second
 * because it costs nothing and the drawer's own close button does the same.
 * Neither being present means that script never ran, and a drawer whose script
 * never ran cannot be open, so there is nothing to close.
 */
function closeProfileDrawer(): void {
  if (typeof window.closeSidebar === 'function') {
    window.closeSidebar()
    return
  }
  if (typeof window.forceCloseSidebar === 'function') window.forceCloseSidebar()
}

/**
 * The first name to greet the florist with, or undefined.
 *
 * Read from the drawer element rather than passed in: this module is loaded by
 * both entry points and has no server data of its own, and the drawer is already
 * the one element on the page that knows who is signed in.
 *
 * Exported because the auto-start gate needs the same name from the same place
 * (TourRoot's boot, docs/INTERACTIVE_TUTORIALS_PLAN.md §3.3). A second reader
 * would be a second selector to keep in step with _account_menu.html.erb, and
 * the card is the same card whether the drawer or the gate raised it.
 */
export function firstNameFromDrawer(): string | undefined {
  const drawer = document.getElementById(DRAWER_ID)
  return drawer?.dataset.tourFirstName?.trim() || undefined
}

/**
 * Start (or Redo) a tutorial from the drawer.
 *
 * Redo is not a separate action: the tour never remembers which event it
 * created, so re-running it is starting it — checkpoint 0, a fresh event. The
 * ERB gives both buttons `data-tour-action="start"` and changes only the label.
 *
 * The Welcome card is raised HERE, on whatever page the florist is on. Where the
 * run itself has to happen is the card's problem: TourRoot's Start handler lands
 * on the Events index first, because checkpoint 0's target lives there.
 */
function onDocumentClick(event: MouseEvent): void {
  const target = event.target
  if (!(target instanceof Element)) return

  // closest(), not a check on the target itself: the click usually lands on a
  // text node inside the button, and a browser may hand us any descendant.
  const trigger = target.closest(START_SELECTOR)
  if (!(trigger instanceof HTMLElement)) return

  event.preventDefault()
  closeProfileDrawer()

  const api = window.__blumeTour
  if (!api) {
    console.warn('[tour] the tutorial engine is not on this page')
    return
  }
  api.showWelcome(trigger.dataset.tourId, { firstName: firstNameFromDrawer() })
}

let bound = false

/**
 * Attach the delegated listener, once per document.
 *
 * Called from mountTutorial(), which runs again on every `turbo:load`. The flag
 * is reset by a full page load (the module is re-evaluated) and not by a Turbo
 * visit (it is not), which matches exactly when the listener survives.
 */
export function initTutorialDrawer(): void {
  if (bound) return
  document.addEventListener('click', onDocumentClick)
  bound = true
}
